import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, TextField, Radio, RadioGroup, FormControlLabel, IconButton, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';
import { marketingService, UpdateBroadcastRequest, MarketingBroadcast } from '../../services/marketing.service';

interface EditBroadcastDialogProps {
  open: boolean;
  onClose: () => void;
  broadcast: MarketingBroadcast;
}

const EditBroadcastDialog: React.FC<EditBroadcastDialogProps> = ({ open, onClose, broadcast }) => {
  const [title, setTitle] = useState(broadcast.title);
  const [message, setMessage] = useState(broadcast.message);
  const [sendOption, setSendOption] = useState('schedule');
  const [sendDate, setSendDate] = useState('');
  const [sendHour, setSendHour] = useState('12');
  const [sendMinute, setSendMinute] = useState('00');
  const [sendPeriod, setSendPeriod] = useState<'AM' | 'PM'>('PM');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const date = new Date(broadcast.scheduledAt);
    setSendDate(date.toISOString().split('T')[0]);
    
    // Convert 24-hour to 12-hour format
    let hours = date.getHours();
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert 0 to 12 for midnight
    
    setSendHour(hours.toString());
    setSendMinute(date.getMinutes().toString().padStart(2, '0'));
    setSendPeriod(period);
    setTitle(broadcast.title);
    setMessage(broadcast.message);
  }, [broadcast]);

  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or valid numbers 1-12
    if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 12)) {
      setSendHour(value);
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or valid numbers 0-59
    if (value === '' || (/^\d{1,2}$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 59)) {
      setSendMinute(value);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      let scheduledAt: string | undefined;
      if (sendOption === 'schedule') {
        const dateValue = new Date(sendDate);
        let hour = parseInt(sendHour, 10) || 12;
        
        // Convert 12-hour to 24-hour format
        if (sendPeriod === 'AM') {
          if (hour === 12) hour = 0; // 12 AM = 0 hours
        } else {
          if (hour !== 12) hour += 12; // PM hours except 12 PM
        }
        
        dateValue.setHours(hour);
        dateValue.setMinutes(parseInt(sendMinute, 10) || 0);
        dateValue.setSeconds(0);
        scheduledAt = dateValue.toISOString();
      }
      
      const request: UpdateBroadcastRequest = {
        title,
        message,
        ...(scheduledAt && { scheduledAt })
      };
      
      await marketingService.updateBroadcast(broadcast.id, request);
      toast.success('Broadcast updated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to update broadcast:', error);
      toast.error('Failed to update broadcast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 'var(--palette-light-3-value)',
          boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
          maxWidth: '508px',
          width: '100%',
          m: 'var(--palette-light-6-value)',
        },
      }}
    >
      <Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-h3-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
            Edit broadcast message
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              width: 'var(--palette-light-8-value)',
              height: 'var(--palette-light-8-value)',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                opacity: 0.8,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
          {/* Title */}
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--palette-light-4-value)',
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  padding: '6px 12px',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: 'var(--palette-light-font-size-small-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                transform: 'translate(12px, 6px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(12px, -9px) scale(0.75)',
                },
              },
            }}
            label="Title"
            InputLabelProps={{ shrink: true }}
          />

          {/* Message */}
          <TextField
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 'var(--palette-light-4-value)',
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                '& fieldset': {
                  border: 'none',
                },
                '& textarea': {
                  padding: '6px 12px',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: 'var(--palette-light-font-size-small-value)',
                color: 'var(--palette-light-text-primary-value-hex)',
                transform: 'translate(12px, 6px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(12px, -9px) scale(0.75)',
                },
              },
            }}
            label="Message"
            InputLabelProps={{ shrink: true }}
          />

          {/* Send Option */}
          <RadioGroup value={sendOption} onChange={(e) => setSendOption(e.target.value)} sx={{ display: 'flex', flexDirection: 'row', gap: 'var(--palette-light-8-value)' }}>
            <FormControlLabel
              value="schedule"
              control={
                <Radio
                  sx={{
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    '&.Mui-checked': {
                      color: 'var(--palette-light-brand-primary-value-hex)',
                    },
                  }}
                />
              }
              label="Schedule"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                },
              }}
            />
            <FormControlLabel
              value="now"
              control={
                <Radio
                  sx={{
                    color: 'var(--palette-light-text-secondary-value-hex)',
                    '&.Mui-checked': {
                      color: 'var(--palette-light-brand-primary-value-hex)',
                    },
                  }}
                />
              }
              label="Send now"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                },
              }}
            />
          </RadioGroup>

          {/* Date and Time */}
          <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
            <TextField
              type="date"
              value={sendDate}
              onChange={(e) => setSendDate(e.target.value)}
              fullWidth
              disabled={sendOption === 'now'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 'var(--palette-light-4-value)',
                  backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                  '& fieldset': {
                    border: 'none',
                  },
                  '& input': {
                    padding: '6px 12px',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: 'var(--palette-light-font-size-small-value)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                  transform: 'translate(12px, 6px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(12px, -9px) scale(0.75)',
                  },
                },
              }}
              label="Send on"
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
              <TextField
                value={sendHour}
                onChange={(e) => setSendHour(e.target.value)}
                disabled={sendOption === 'now'}
                sx={{
                  width: '80px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--palette-light-4-value)',
                    backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '12px',
                      fontSize: 'var(--palette-light-font-size-default-value)',
                      textAlign: 'center',
                    },
                  },
                }}
              />
              <TextField
                value={sendMinute}
                onChange={(e) => setSendMinute(e.target.value)}
                disabled={sendOption === 'now'}
                sx={{
                  width: '80px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 'var(--palette-light-4-value)',
                    backgroundColor: sendOption === 'now' ? 'var(--palette-light-background-tertiary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      padding: '12px',
                      fontSize: 'var(--palette-light-font-size-default-value)',
                      textAlign: 'center',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-4-value)' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            fullWidth
            sx={{
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: 'var(--palette-light-background-tertiary-value-hex)',
                color: 'var(--palette-light-text-tertiary-value-hex)',
              },
            }}
          >
            {loading ? 'Saving...' : 'Save changes'}
          </Button>
          <Button
            variant="text"
            onClick={onClose}
            fullWidth
            sx={{
              color: 'var(--palette-light-brand-primary-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditBroadcastDialog;
